import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Drawer, DrawerProps, message as antdMessage } from 'antd';
import StatusButton from 'components/StatusButton';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useCrud from 'hooks/useCrud';
import dayjs from 'lib/dayjs';
import { ReactNode, useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { caseDetail, closeCase, getAgentFeedbackMessages } from 'services/api/feedback';
import GalaxyImplement from 'views/Galaxy/GalaxyImplement';
import { CategoryMap } from '../hooks/useCaseCategory';
import { getCategoryLabel } from './FeedbackTable';
import OperateLog from './OperateLog';
import { ReFeedback } from './ReFeedback';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% + 48px);
  margin: -24px;
  overflow: hidden;
  > div {
    flex: 1 0 0;
    padding: 16px;
    overflow: auto;
  }
  > footer {
    flex: 0 0 auto;
  }
`;

const Wrapper = styled.div`
  margin-bottom: 16px;
  > h3 {
    display: flex;
    align-items: center;
    position: relative;
    height: 22px;
    margin-bottom: 14px;
    text-indent: 16px;
    font-size: 14px;
    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 3px;
      left: 0;
      width: 4px;
      height: 16px;
      background: var(--fett-colors-primary);
    }
  }
  > div {
    margin-left: 16px;
  }
`;

const Galaxy = styled(GalaxyImplement)`
  height: 600px;
  border: solid var(--fett-border-default) 1px;
`;

const Conversation = styled.div`
  max-height: 600px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  background: var(--fett-chat-bubble-system);
  border-radius: 12px;
`;

const ConversationTurn = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  min-width: 0;
  max-width: 88%;
  > header {
    margin-bottom: 6px;
    font-size: 12px;
    color: var(--fett-text-secondary);
  }
  > p {
    max-width: 100%;
    margin: 0;
    padding: 12px 16px;
    border-radius: 12px;
    background: var(--fett-bg-panel);
    color: var(--fett-text-primary);
    line-height: 1.7;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  &[data-role='user'] {
    align-self: flex-end;
    align-items: flex-end;
    > p {
      background: var(--fett-bg-active);
    }
  }
  &[data-feedback-target='true'] > p {
    background: var(--fett-chat-bubble-agent);
  }

`;

const BasicInfo = styled.div`
  display: flex;
  > dl {
    flex: 1 0 0;
    display: flex;
    margin-bottom: 4px;
    > dt {
      margin-right: 8px;
      white-space: nowrap;
    }
    > dd {
      margin-bottom: 0;
      word-break: break-all;
    }
    > * {
      font-weight: 400;
      color: var(--fett-text-secondary);
    }
  }
`;

const Footer = styled.footer`
  border-top: 1px solid var(--fett-border-default);
`;

const ExtraTag = styled.span`
  display: inline-block;
  height: 24px;
  line-height: 24px;
  margin-top: 6px;
  padding: 0 8px;
  font-size: 12px;
  color: var(--fett-surface-warning-text);
  background: var(--fett-surface-warning-bg);
  border-radius: 2px;
`;

export interface FeedbackDrawerProps extends DrawerProps {
  caseId: number;
  categoryMap: CategoryMap;
  onCaseUpdated?: () => void;
}

function FeedbackDrawer(props: FeedbackDrawerProps) {
  const { visible, caseId, categoryMap, onCaseUpdated, ...otherProps } = props;

  const feedbackReplyRef = useRef<HTMLElement | null>(null);

  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();

  const {
    loading,
    item: detail,
    fetch: fetchDetail,
  } = useCrud({
    read: caseDetail,
  });

  const refsCurrent = {
    t,
    fetchDetail,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (!visible) {
      return;
    }

    const {
      current: { t, fetchDetail },
    } = refs;

    fetchDetail(caseId).catch(() => {
      antdMessage.error(t('feedback.获取反馈详情失败，请稍后再试'));
    });
  }, [visible, caseId]);

  const drawerProps: DrawerProps = {
    visible,
    title: t('feedback.详情'),
    width: 720,
    ...otherProps,
  };

  if (!detail || detail.caseBasicInfo.caseId !== caseId) {
    return <Drawer {...drawerProps}>{loading && <LoadingOutlined />}</Drawer>;
  }
  const { caseBasicInfo, operateLog } = detail;

  const { caseType, createTime, reason, message, caseStatus } = caseBasicInfo;

  const log =
    operateLog.length === 0 ? null : (
      <OperateLog
        message={message}
        logList={operateLog}
        render={(content, translateButton) => {
          return (
            <Wrapper>
              <h3>
                {t('feedback.修复记录')}
                {translateButton}
              </h3>
              {content}
            </Wrapper>
          );
        }}
      />
    );

  const basicInfo = (
    <Wrapper>
      <h3>{t('feedback.基本信息')}</h3>
      <BasicInfo>
        <dl>
          <dt>{t('feedback.类型')}:</dt>
          <dd>
            {
              {
                UN_ANSWERED: t('feedback.未识别'),
                WRONG_ANSWER: t('feedback.回复错误'),
                ACTIVITY: t('feedback_of_activity'),
                AGENT_REPLY: t('agent.feedbackCaseType'),
              }[caseType]
            }
          </dd>
        </dl>
        <dl>
          <dt>ID:</dt>
          <dd>{caseId}</dd>
        </dl>
      </BasicInfo>
      <BasicInfo>
        <dl>
          <dt>{t('feedback.分类')}:</dt>
          <dd>
            {getCategoryLabel(caseBasicInfo, categoryMap, systemLanguage) ||
              t('agent.feedbackMissingReason')}
          </dd>
        </dl>
        <dl>
          <dt>{t('feedback.提交时间')}:</dt>
          <dd>{dayjs(createTime).format('YYYY-MM-DD HH:mm:ss')}</dd>
        </dl>
      </BasicInfo>
      {reason ? (
        <BasicInfo>
          <dl>
            <dt>{t('feedback.描述')}:</dt>
            <dd>{reason}</dd>
          </dl>
        </BasicInfo>
      ) : null}
    </Wrapper>
  );

  let galaxy: ReactNode;

  try {
    const { conversationId, msgId, sendTime } = JSON.parse(message);

    galaxy = conversationId && (
      <Wrapper>
        <h3>{t('feedback.会话记录')}</h3>
        <Galaxy
          key={caseId}
          visible={visible}
          conversationId={conversationId}
          searchMessage={{
            msgId,
            sendTime: `${sendTime}`,
          }}
          targetMessageExtra={<ExtraTag>{t('feedback.反馈内容')}</ExtraTag>}
        />
      </Wrapper>
    );
  } catch (err) {
    galaxy = null;
  }

  const footer = caseStatus === 'FIXED' && (
    <Footer className="flex items-center h-12 px-4 gap-4">
      <StatusButton
        type="primary"
        onClick={() => {
          return closeCase(caseId)
            .then(() => {
              antdMessage.success(t('feedback.关闭问题成功'));

              if (onCaseUpdated) {
                onCaseUpdated();
              }

              fetchDetail(caseId).catch(() => {
                antdMessage.error(t('feedback.获取反馈详情失败，请稍后再试'));
              });
            })
            .catch(() => {
              antdMessage.error(t('feedback.关闭问题失败，请稍后再试'));
            });
        }}
      >
        {t('feedback_btn_solved')}
      </StatusButton>
      <ReFeedback
        caseId={caseId}
        onFinish={() => {
          fetchDetail(caseId).catch(() => {
            antdMessage.error(t('feedback.获取反馈详情失败，请稍后再试'));
          });
        }}
      />
    </Footer>
  );

  const content = (
    <Container>
      <div>
        {log}
        {basicInfo}
        {caseType === 'AGENT_REPLY' && caseBasicInfo.agentContext ? (
          <>
            <Wrapper>
              <h3>{t('feedback.会话记录')}
                <Button type="link" size="small" style={{ marginLeft: 'auto', textIndent: 0 }}
                  onClick={() => feedbackReplyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                  {t('agent.feedbackLocateReply')}
                </Button>
              </h3>
              <Conversation aria-label={t('feedback.会话记录')}>
                {getAgentFeedbackMessages(caseBasicInfo).map((message) => (
                  <ConversationTurn key={message.id} data-role={message.role}
                    ref={message.id === caseBasicInfo.agentContext?.messagePublicId ? feedbackReplyRef : undefined}
                    data-feedback-target={message.id === caseBasicInfo.agentContext?.messagePublicId}
                    aria-label={t(message.role === 'user' ? 'agent.feedbackQuestion' : 'agent.feedbackAnswer')}>
                    <header>{t(message.role === 'user' ? 'agent.feedbackQuestion' : 'agent.feedbackAnswer')}</header>
                    <p>{message.content || '-'}</p>
                  </ConversationTurn>
                ))}
              </Conversation>
            </Wrapper>
            <Wrapper>
              <h3>{t('agent.feedbackAllReasons')}</h3>
              <div>
                {caseBasicInfo.agentContext.categories
                  ?.map((category) => category.name)
                  .join(' / ') || t('agent.feedbackMissingReason')}
              </div>
            </Wrapper>
          </>
        ) : (
          galaxy
        )}
      </div>
      {footer}
    </Container>
  );

  return (
    <Drawer {...drawerProps} zIndex={1999}>
      {content}
    </Drawer>
  );
}

export default FeedbackDrawer;
